<script setup>
import { defineEmits, defineProps, reactive } from "vue";
import useUserService from "@/composables/useUserService";
import { updateReview } from "@/api";
import useModal from "@/composables/useModal";

const { userId } = useUserService();
const { alert } = useModal();

const emits = defineEmits(["submit", "close"]);
const props = defineProps({
  reviewTarget: {
    type: Object,
    default: () => {},
  },
});
const escapeRegex = (string) => {
  if (string) {
    return string.replace(/[-/\\^$%*+?.()|[\]{}<>]/g, " ");
  } else {
    return string;
  }
};

const requestData = reactive({
  memberId: userId.value,
  productReviewId: props.reviewTarget.id,
  starPoint: String(props.reviewTarget.starPoint),
  reviewDetail: props.reviewTarget.reviewDetail,
});

const upLoadFile = reactive([
  {
    attachFile: null,
    attachFileName: null,
    fileuuid: props.reviewTarget.userImage1UploadFileJson?.id,
    real_url: props.reviewTarget.userImage1UploadFileJson?.realUrl,
  },
  {
    attachFile: null,
    attachFileName: null,
    fileuuid: props.reviewTarget.userImage2UploadFileJson?.id,
    real_url: props.reviewTarget.userImage2UploadFileJson?.realUrl,
  },
  {
    attachFile: null,
    attachFileName: null,
    fileuuid: props.reviewTarget.userImage3UploadFileJson?.id,
    real_url: props.reviewTarget.userImage3UploadFileJson?.realUrl,
  },
]);
const validate = () => {
  if (requestData.starPoint === "0") {
    alert("별점 선택은 필수입니다");
    return false;
  }

  if (requestData.reviewDetail === "" || requestData.reviewDetail == null) {
    alert("리뷰 내용은 필수입니다");
    return false;
  }

  return true;
};
const reviewWriteRequest = async () => {
  let chooseOption = "";
  props.reviewTarget.selectors.forEach((item, index) => {
    if (index != 0) {
      chooseOption += ` / `;
    }
    chooseOption += `${item.value}: ${
      document.querySelector("#choose" + index).value
    }`;
  });

  if (!validate()) {
    return;
  }

  await updateReview({
    memberId: requestData.memberId,
    productReviewId: requestData.productReviewId,
    starPoint: Number(requestData.starPoint),
    userImage1: upLoadFile[0].fileuuid ? upLoadFile[0].fileuuid : null,
    userImage2: upLoadFile[1].fileuuid ? upLoadFile[1].fileuuid : null,
    userImage3: upLoadFile[2].fileuuid ? upLoadFile[2].fileuuid : null,
    reviewDetail: escapeRegex(requestData.reviewDetail),
    chooseJson: chooseOption,
  });

  await alert("수정이 완료되었습니다.");
  emits("submit", true);
};

const fileValidate = (file) => {
  const regex = /\.(gif|jpe?g|png|)$/i;
  if (!regex.test(file)) {
    alert("유효하지 않은 확장자입니다.");
    return false;
  }

  return true;
};
// 파일 컨트롤러
const onFileChange = (idx, e) => {
  upLoadFile[idx].attachFileName = e.target.files[0].name; //.files
  upLoadFile[idx].attachFile = e.target.files[0];
  if (!fileValidate(upLoadFile[idx].attachFileName)) {
    return;
  }

  uploadAttachment(idx, upLoadFile[idx].attachFile);
  const inputFile = document.getElementById(`reviewFile${idx}`);
  inputFile.value = "";
};

const uploadAttachment = async (idx, file) => {
  let formData = new FormData();
  formData.append("attach", file); // 무조건 form에 attach 해서 보내야 하는가??

  await fetch("/gooduser/api/v1/attachment/upload", {
    method: "POST",
    body: formData,
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.message === "SUCCESS") {
        upLoadFile[idx].fileuuid = data.info.uuid;
        upLoadFile[idx].real_url = data.info.real_url;
      } else {
        deleteFile();
        return alert(data.message);
      }
    })
    .catch((error) => {
      deleteFile();
      alert("오류가 발생했습니다: " + error);
      console.error(error);
    });
};
const deleteFile = (idx) => {
  upLoadFile[idx].attachFile = null;
  upLoadFile[idx].attachFileName = null;
  upLoadFile[idx].fileuuid = null;
};
</script>

<template>
  <div class="popup-bg on" @click="emits('close')"></div>
  <div class="popup-cont w01 on" id="popup-review">
    <div class="popup-header">
      <h3 class="title">상품리뷰</h3>
      <button type="button" class="popup-close-btn" @click="emits('close')">
        닫기
      </button>
    </div>
    <div class="popup-body">
      <div class="goods_area">
        <div class="goods_img">
          <div
            class="img"
            :style="`background-image: url(${props.reviewTarget.product.mainImageUrl})`"
          >
            {{
              props.reviewTarget.product.textName
                ? props.reviewTarget.product.textName
                : props.reviewTarget.product.name
            }}
          </div>
        </div>
        <p>
          {{
            props.reviewTarget.product.textName
              ? props.reviewTarget.product.textName
              : props.reviewTarget.product.name
          }}
        </p>
      </div>
      <div class="popup_write">
        <div class="star_area starChk">
          <input
            type="radio"
            name="ratingChk"
            id="rating0"
            value="0"
            v-model="requestData.starPoint"
          />
          <label for="rating0" class="chk chk00">0점</label>
          <input
            type="radio"
            name="ratingChk"
            id="rating1"
            value="1"
            v-model="requestData.starPoint"
          />
          <label for="rating1" class="chk chk01">1점</label>
          <input
            type="radio"
            name="ratingChk"
            id="rating2"
            value="2"
            v-model="requestData.starPoint"
          />
          <label for="rating2" class="chk chk02">2점</label>
          <input
            type="radio"
            name="ratingChk"
            id="rating3"
            value="3"
            v-model="requestData.starPoint"
          />
          <label for="rating3" class="chk chk03">3점</label>
          <input
            type="radio"
            name="ratingChk"
            id="rating4"
            value="4"
            v-model="requestData.starPoint"
          />
          <label for="rating4" class="chk chk04">4점</label>
          <input
            type="radio"
            name="ratingChk"
            id="rating5"
            value="5"
            v-model="requestData.starPoint"
          />
          <label for="rating5" class="chk chk05">5점</label>
        </div>
        <div class="input_wrap">
          <form action="">
            <div
              v-for="(item, index) in props.reviewTarget.selectors"
              :key="index"
            >
              <p class="input_tit">{{ item.value }}</p>
              <div class="input_area">
                <select :name="`choose${index}`" :id="`choose${index}`">
                  <option
                    :value="choose.optionName"
                    v-for="(choose, index2) in item.items"
                    :key="index2"
                  >
                    {{ choose.optionName }}
                  </option>
                </select>
              </div>
            </div>
            <p class="input_tit">상세리뷰<strong class="required">*</strong></p>
            <div class="input_area">
              <textarea
                name="reviewDetail"
                id="reviewDetail"
                v-model="requestData.reviewDetail"
                :placeholder="
                  props.reviewTarget?.productCategory?.reviewPlaceholder
                "
              ></textarea>
            </div>
            <p class="input_tit">사진첨부</p>
            <div class="attach_area">
              <ul>
                <li>
                  <div class="attach">
                    <input
                      type="file"
                      name="reviewFile0"
                      id="reviewFile0"
                      @change="onFileChange(0, $event)"
                      accept=".gif, .jpg, .jpeg, .png"
                    />
                    <label for="reviewFile0" class="fileBtn">사진첨부</label>
                  </div>
                  <div class="img_area" v-if="upLoadFile[0].fileuuid">
                    <div
                      class="img"
                      :style="`background-image: url(${upLoadFile[0].real_url});`"
                    ></div>
                    <button
                      type="button"
                      class="btn delete"
                      @click="deleteFile(0)"
                    >
                      삭제
                    </button>
                  </div>
                </li>
                <li>
                  <div class="attach">
                    <input
                      type="file"
                      name="reviewFile1"
                      id="reviewFile1"
                      @change="onFileChange(1, $event)"
                      accept=".gif, .jpg, .jpeg, .png"
                    />
                    <label for="reviewFile1" class="fileBtn">사진첨부</label>
                  </div>
                  <div class="img_area" v-if="upLoadFile[1].fileuuid">
                    <div
                      class="img"
                      :style="`background-image: url(${upLoadFile[1].real_url});`"
                    ></div>
                    <button
                      type="button"
                      class="btn delete"
                      @click="deleteFile(1)"
                    >
                      삭제
                    </button>
                  </div>
                </li>
                <li>
                  <div class="attach">
                    <input
                      type="file"
                      name="reviewFile2"
                      id="reviewFile2"
                      @change="onFileChange(2, $event)"
                      accept=".gif, .jpg, .jpeg, .png"
                    />
                    <label for="reviewFile2" class="fileBtn">사진첨부</label>
                  </div>
                  <div class="img_area" v-if="upLoadFile[2].fileuuid">
                    <div
                      class="img"
                      :style="`background-image: url(${upLoadFile[2].real_url});`"
                    ></div>
                    <button
                      type="button"
                      class="btn delete"
                      @click="deleteFile(2)"
                    >
                      삭제
                    </button>
                  </div>
                </li>
              </ul>
              <p class="attach_txt">
                사진은 최대 20MB 이하의 JPG, PNG, GIF 파일 3개까지 첨부
                가능합니다.
              </p>
            </div>
            <p class="input_notice">
              권리침해, 욕설, 특정 대상을 비하하는 내용, 청소년에게 유해한 내용
              등을 게시할 경우 운영정책과 이용약관 및 관련 법률에 의해 제재될 수
              있습니다.
            </p>
            <div class="btn_area">
              <button
                type="button"
                class="btn form02"
                @click="reviewWriteRequest"
              >
                저장
              </button>
              <button
                type="button"
                class="btn popup-close"
                @click="emits('close')"
              >
                취소
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
